import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sprint Arquitectura Definitiva #3 · Backend switch para admins.
// El admin puede alternar entre Producción y Desarrollo desde /perfil.
// Cuando `nexus.backend` == 'dev' y REACT_APP_BACKEND_URL_DEV está
// disponible, dirigimos las requests ahí; en caso contrario usamos el
// backend por defecto (producción). El switch persiste en localStorage
// y hace `location.reload()` para tomar efecto de inmediato.
function _resolveBaseUrl(): string {
  let base = BACKEND_URL;
  try {
    const pref = window.localStorage.getItem('nexus.backend');
    if (pref === 'dev') {
      const devUrl = (process.env.REACT_APP_BACKEND_URL_DEV as string | undefined) || '';
      if (devUrl) base = devUrl;
    }
  } catch { /* SSR / private mode */ }
  return `${base}/api`;
}

const API_BASE = _resolveBaseUrl();

// Emergent Support fix (2026-06-30):
//   - The URL that officially validates a session is `/auth/me`. Only a 401
//     coming from THAT endpoint may clear the local token. A 401 from any
//     other endpoint is treated as a per-request auth failure; the session
//     stays intact so a single slow query or transient issue no longer
//     kicks the user out (root cause of "random logouts in production").
//   - Timeouts and network errors (no `error.response`) are retried once
//     automatically. If the retry still fails we surface the error but
//     never touch the token.
//
// Sprint v28 update (2026-02-21):
//   - Added SILENT JWT REFRESH: on every request we check whether the
//     token is < 24h from expiry and refresh in the background. When
//     refresh fails we fall through to the existing 401 handling.
//   - Added COLD-START RESILIENCE: 502/503/504 responses are retried
//     with exponential backoff (up to 3 attempts) so the "spinning up
//     servers" state is transparent to the user.
const AUTH_VALIDATION_ENDPOINT = '/auth/me';
const RETRY_FLAG = '__nexusRetried' as const;
const RETRY_COUNT = '__nexusRetryCount' as const;
const MAX_COLDSTART_RETRIES = 3;

// Refresh the token when it has < REFRESH_WINDOW_MS left before expiry.
const REFRESH_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h

type RetriableConfig = InternalAxiosRequestConfig & {
  [RETRY_FLAG]?: boolean;
  [RETRY_COUNT]?: number;
};

/** Decode the exp claim from a JWT without a lib dependency. Returns
 * epoch ms or null if the token is opaque / malformed. */
function decodeTokenExp(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    if (typeof json.exp === 'number') return json.exp * 1000;
    return null;
  } catch {
    return null;
  }
}

// Prevent multiple concurrent refreshes.
let refreshInFlight: Promise<string | null> | null = null;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests + trigger silent refresh if near expiry.
    this.api.interceptors.request.use(async (config) => {
      let token = localStorage.getItem('nexus_token');
      const url = (config.url || '').toString();
      const isRefreshCall = url.endsWith('/auth/refresh');

      // Silent refresh window check (skip on the refresh endpoint itself
      // to avoid recursion).
      if (token && !isRefreshCall) {
        const expMs = decodeTokenExp(token);
        const now = Date.now();
        if (expMs && expMs - now > 0 && expMs - now < REFRESH_WINDOW_MS) {
          const fresh = await this.tryRefresh(token);
          if (fresh) token = fresh;
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Sprint Pulido Final · Strict i18n. Every backend call carries
      // the user's picker locale so narrations, LLM system prompts and
      // Delivery Card fallbacks match the UI language.
      try {
        const loc = window.localStorage.getItem('nexus.locale.v2')
          || window.localStorage.getItem('nexus.locale')
          || 'es';
        if (loc === 'es' || loc === 'en') {
          config.headers['X-User-Locale'] = loc;
        }
      } catch { /* SSR/private mode: ignore */ }

      return config;
    });

    // Response interceptor — targeted logout + resilient retries
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = (error.config || {}) as RetriableConfig;

        // 1) Transient errors (timeout / network / no response) → retry ONCE.
        //    Never clear the token here.
        const isTimeout =
          error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
        const isNetwork = !error.response && !isTimeout;
        if ((isTimeout || isNetwork) && !config[RETRY_FLAG]) {
          config[RETRY_FLAG] = true;
          return this.api.request(config);
        }

        // 2) v28 · Cold-start (502/503/504) → exponential backoff up to
        //    MAX_COLDSTART_RETRIES. Emergent's container wake-up can
        //    take 6-15 s; without this the user sees ugly errors.
        const status = error.response?.status;
        if (status === 502 || status === 503 || status === 504) {
          const attempts = (config[RETRY_COUNT] || 0) + 1;
          if (attempts <= MAX_COLDSTART_RETRIES) {
            config[RETRY_COUNT] = attempts;
            const backoffMs = Math.min(
              8000, 500 * Math.pow(2, attempts - 1),
            );
            await new Promise((r) => setTimeout(r, backoffMs));
            return this.api.request(config);
          }
        }

        // 3) 401 → only clear the session when it comes from the auth-
        //    validation endpoint. That is the only signal that confirms
        //    the token itself is invalid/expired.
        if (error.response?.status === 401) {
          const url = (config.url || '').toString();
          const isAuthValidation = url.endsWith(AUTH_VALIDATION_ENDPOINT);
          // v28 · Try a last-chance silent refresh before kicking out.
          if (isAuthValidation) {
            const token = localStorage.getItem('nexus_token');
            const fresh = token ? await this.tryRefresh(token) : null;
            if (fresh) {
              if (!config.headers) {
                config.headers = new axios.AxiosHeaders();
              }
              (config.headers as any).Authorization = `Bearer ${fresh}`;
              return this.api.request(config);
            }
            localStorage.removeItem('nexus_token');
            // Only redirect if we're not already on the auth page.
            if (
              typeof window !== 'undefined' &&
              !window.location.pathname.startsWith('/auth')
            ) {
              window.location.href = '/auth';
            }
          }
          // For any other endpoint we keep the session; caller handles the
          // error (e.g. shows a retry banner).
        }

        return Promise.reject(error);
      },
    );
  }

  /** v28 · Silent token refresh with in-flight de-duplication. */
  private async tryRefresh(currentToken: string): Promise<string | null> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const resp = await axios.post(
          `${API_BASE}/auth/refresh`, {},
          {
            headers: { Authorization: `Bearer ${currentToken}` },
            timeout: 10000,
          },
        );
        const newToken = resp.data?.access_token as string | undefined;
        if (newToken) {
          localStorage.setItem('nexus_token', newToken);
          return newToken;
        }
        return null;
      } catch {
        return null;
      } finally {
        // Small window before clearing so overlapping requests share it.
        setTimeout(() => { refreshInFlight = null; }, 100);
      }
    })();
    return refreshInFlight;
  }

  /** Public wrapper: refresh the token now (used before opening Preview
   * so the JWT embedded in the runtime-proxy URL is always fresh). */
  async refreshToken(): Promise<string | null> {
    const token = localStorage.getItem('nexus_token') || '';
    if (!token) return null;
    return this.tryRefresh(token);
  }

  // Auth
  async register(email: string, password: string, name: string) {
    const response = await this.api.post('/auth/register', { email, password, name });
    return response.data;
  }

  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async getMe() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async updateProfile(patch: { name?: string; email?: string; avatar_url?: string; bio?: string; company?: string }) {
    const r = await this.api.patch('/auth/me', patch);
    return r.data;
  }

  async changePassword(current_password: string, new_password: string) {
    const r = await this.api.post('/auth/change-password', { current_password, new_password });
    return r.data;
  }

  async uploadAvatar(dataUrl: string) {
    const r = await this.api.post('/auth/avatar', { data_url: dataUrl });
    return r.data;
  }

  async createTicket(subject: string, message: string, category = 'general', workspace_id?: string) {
    const r = await this.api.post('/tickets', { subject, message, category, workspace_id });
    return r.data;
  }

  async listMyTickets() {
    const r = await this.api.get('/tickets');
    return r.data;
  }

  async getTicket(ticketId: string) {
    const r = await this.api.get(`/tickets/${ticketId}`);
    return r.data;
  }

  async replyTicket(ticketId: string, message: string, attachments?: any[]) {
    const r = await this.api.post(`/tickets/${ticketId}/reply`, { message, attachments });
    return r.data;
  }

  async adminListTickets(status?: string) {
    const r = await this.api.get('/admin/tickets', { params: status ? { status } : {} });
    return r.data;
  }

  async adminReplyTicket(ticketId: string, message: string, close = false, attachments?: any[]) {
    const r = await this.api.post(`/admin/tickets/${ticketId}/reply`, { message, close, attachments });
    return r.data;
  }

  async forgotPassword(email: string) {
    const r = await this.api.post('/auth/forgot-password', { email });
    return r.data as { ok: boolean; message: string; reset_link?: string };
  }

  async resetPassword(token: string, new_password: string) {
    const r = await this.api.post('/auth/reset-password', { token, new_password });
    return r.data;
  }

  async exchangeGoogleSession(session_id: string) {
    // Exchanges an Emergent-managed Google Auth session_id for our own JWT.
    const r = await this.api.post('/auth/google', { session_id });
    return r.data as {
      access_token: string;
      token_type: string;
      default_workspace_id?: string | null;
      user: any;
    };
  }

  // Workspaces
  async createWorkspace(data: any) {
    const response = await this.api.post('/workspaces', data);
    return response.data;
  }

  async listWorkspaces(status?: string) {
    const params = status ? { status } : {};
    const response = await this.api.get('/workspaces', { params });
    return response.data;
  }

  async getWorkspace(id: string) {
    const response = await this.api.get(`/workspaces/${id}`);
    return response.data;
  }

  async updateWorkspace(id: string, data: any) {
    const response = await this.api.patch(`/workspaces/${id}`, data);
    return response.data;
  }

  async deleteWorkspace(id: string) {
    const response = await this.api.delete(`/workspaces/${id}`);
    return response.data;
  }

  // ── v19 · Workspace lifecycle actions ────────────────────────────
  async duplicateWorkspace(id: string) {
    const response = await this.api.post(`/workspaces/${id}/duplicate`);
    return response.data;
  }

  async archiveWorkspace(id: string) {
    // Soft-delete: hard=false marks the workspace as archived without cascading.
    const response = await this.api.delete(`/workspaces/${id}?hard=false`);
    return response.data;
  }

  exportWorkspaceUrl(id: string): string {
    // Returns the absolute URL so the browser can download directly
    // (with the Authorization header via a fetch + blob dance).
    return `${API_BASE}/workspaces/${id}/export`;
  }

  async downloadWorkspaceZip(id: string, filename?: string) {
    const token = localStorage.getItem('nexus_token');
    const resp = await fetch(this.exportWorkspaceUrl(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `Export failed (${resp.status})`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `workspace-${id.slice(0, 8)}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ── v19 · Deployments (global list, decoupled from workspaces) ───
  async listDeployments(includeInactive = false) {
    const response = await this.api.get('/deployments', {
      params: { include_inactive: includeInactive },
    });
    return response.data as Array<{
      workspace_id: string;
      workspace_name: string;
      workspace_description?: string | null;
      slug: string;
      version: number;
      provider: string;
      provider_label: string;
      url: string | null;
      deployment_url?: string | null;
      url_versioned: string | null;
      status: string;
      environment: string;
      published_at: string;
      published_by: string | null;
      size_bytes: number;
      project_stack: string | null;
      tech_stack: string[];
      thumbnail_url: string | null;
      fallback_reason: string | null;
    }>;
  }

  async listWorkspaceDeployments(workspaceId: string) {
    const response = await this.api.get(`/deployments/${workspaceId}`);
    return response.data;
  }

  async unpublishWorkspaceDeployment(workspaceId: string) {
    const response = await this.api.delete(`/deployments/${workspaceId}`);
    return response.data;
  }

  async redeployWorkspace(workspaceId: string, target?: string) {
    const response = await this.api.post(
      `/deployments/${workspaceId}/redeploy`,
      { target, version_bump: true },
    );
    return response.data;
  }


  async getWorkspaceSnapshotStatus(id: string) {
    const response = await this.api.get(`/workspaces/${id}/snapshot/status`);
    return response.data as { workspace_id: string; files: number; durable: boolean };
  }

  async snapshotWorkspace(id: string) {
    const response = await this.api.post(`/workspaces/${id}/snapshot`);
    return response.data;
  }

  async restoreWorkspaceSnapshot(id: string) {
    const response = await this.api.post(`/workspaces/${id}/snapshot/restore`);
    return response.data;
  }

  // ── Preferences / custom backgrounds ────────────────────────────
  async getPreferences() {
    const response = await this.api.get('/preferences');
    return response.data;
  }

  async uploadBackground(slot: 'auth' | 'home' | 'workspace', file: File) {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post(
      `/preferences/backgrounds/${slot}`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async deleteBackground(slot: 'auth' | 'home' | 'workspace') {
    const response = await this.api.delete(`/preferences/backgrounds/${slot}`);
    return response.data;
  }

  // ── Build orchestrator: pause & resume ──────────────────────────
  async resumeBuild(workspaceId: string) {
    const response = await this.api.post(`/workspaces/${workspaceId}/build/resume`);
    return response.data as { status: string; job_id?: string };
  }

  async getBuildStatus(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/build/status`);
    return response.data;
  }

  // ── Publish flow ────────────────────────────────────────────────
  async publishReadiness(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/publish/readiness`);
    return response.data as {
      ready: boolean;
      project_kind: string;
      checks: Array<{ name: string; ok: boolean; hint?: string }>;
    };
  }

  async publishWorkspace(workspaceId: string, target: string = 'auto') {
    // Sprint P0.5 v1.48 · Surface the backend `detail` (readiness
    // failure hint, missing files, etc.) instead of the generic Axios
    // "Request failed with status code 400". Users saw a bare 400 and
    // could not diagnose the actual publish problem.
    try {
      const response = await this.api.post(
        `/workspaces/${workspaceId}/publish`,
        { target },
      );
      return response.data as {
        ok: boolean; slug: string; version: number;
        url: string; url_versioned: string;
        external_url: string | null;
        size_bytes: number; files_count: number;
        target: string; project_kind: string;
        published_at: string;
        external?: {
          url: string | null;
          target: string | null;
          app: string | null;
          region: string | null;
          logs_tail?: string | null;
          fallback_reason: string | null;
        } | null;
      };
    } catch (err: any) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || 'La publicación no pudo completarse.';
      const enriched = new Error(String(detail));
      (enriched as any).status = err?.response?.status;
      (enriched as any).original = err;
      throw enriched;
    }
  }

  async publishTargets(workspaceId: string) {
    const response = await this.api.get(
      `/workspaces/${workspaceId}/publish/targets`,
    );
    return response.data as {
      targets: Array<{
        id: string; label: string; available: boolean; hint?: string;
      }>;
    };
  }

  async publishHistory(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/publish/history`);
    return response.data as { versions: any[] };
  }

  async publishRollback(workspaceId: string, version: number) {
    const response = await this.api.post(`/workspaces/${workspaceId}/publish/rollback/${version}`);
    return response.data;
  }

  // Sprint P0 Quirúrgico Feb 2026 · Preview Rollback (workspace-level).
  // Distinto de publishRollback (que revierte una publicación). Este
  // restaura el último snapshot estable del scaffold en Preview.
  // JAMÁS refunde créditos.
  async previewRollbackHistory(workspaceId: string) {
    const r = await this.api.get(`/workspaces/${workspaceId}/rollback/history`);
    return r.data as { workspace_id: string; versions: any[] };
  }

  async previewRollback(workspaceId: string, versionId?: string) {
    const r = await this.api.post(
      `/workspaces/${workspaceId}/rollback`,
      versionId ? { version_id: versionId } : {},
    );
    return r.data as {
      workspace_id: string;
      restored: boolean;
      version_id?: string;
      created_at?: string;
      bytes?: number;
      credits_refunded: false;
    };
  }

  // Aliases used by Aplicaciones page (Último Deploy).
  listPublishedVersions = (workspaceId: string) => this.publishHistory(workspaceId);
  rollbackVersion = (workspaceId: string, version: number) => this.publishRollback(workspaceId, version);

  async analyzePromptPhases(prompt: string) {
    // Smart Phase Planning — Último Deploy.
    const r = await this.api.post('/planner/analyze', { prompt });
    return r.data as {
      needs_confirmation: boolean;
      phases: Array<{ order: number; name: string; description: string }>;
      reason: string;
    };
  }

  // Sprint Arquitectura Definitiva · Home Apps
  async getHomeApps() {
    const r = await this.api.get('/home/apps');
    return r.data as { deployed: any[]; pinned: any[] };
  }

  async setPinnedApps(pinned: string[]) {
    const r = await this.api.put('/home/apps/pinned', { pinned });
    return r.data as { ok: boolean; pinned: string[] };
  }

  async setAppThumbnailHint(workspaceId: string, hint: string | null) {
    const r = await this.api.put(`/home/apps/${workspaceId}/thumbnail-hint`, { hint });
    return r.data as { ok: boolean; hint: string | null };
  }

  // Conversation Architect — replaced by /architect/chat v2 (see below,
  // "Sprint Arquitecto IA" section). Legacy alias kept out to avoid
  // dupes in TS build.

  // Design Builder
  async designBuild(payload: {
    session_id?: string; workspace_id?: string;
    image: { data_url?: string; url?: string; name?: string };
    intent?: 'EXACT_REPLICA' | 'STRONG_INSPIRATION' | 'LOOSE_INSPIRATION';
    objective?: string;
    max_iterations?: number;
  }) {
    const r = await this.api.post('/design-builder/build', payload);
    return r.data;
  }


  async listMissionEvents(missionId: string, since: number = 0) {
    const response = await this.api.get(`/missions/${missionId}/events?since=${since}`);
    return response.data;
  }

  async getMissionDocument(missionId: string) {
    const response = await this.api.get(`/missions/${missionId}/document`);
    return response.data;
  }

  // Missions
  async createMission(data: any) {
    const response = await this.api.post('/missions', data);
    return response.data;
  }

  async listMissions(workspaceId?: string, status?: string) {
    const params: any = {};
    if (workspaceId) params.workspace_id = workspaceId;
    if (status) params.status = status;
    const response = await this.api.get('/missions', { params });
    return response.data;
  }

  async getMission(id: string) {
    const response = await this.api.get(`/missions/${id}`);
    return response.data;
  }

  async updateMission(id: string, data: any) {
    const response = await this.api.patch(`/missions/${id}`, data);
    return response.data;
  }

  async submitMissionAnswers(id: string, answers: Record<string, string>) {
    const response = await this.api.post(`/missions/${id}/answer`, { answers });
    return response.data;
  }

  // Deployment
  async listDeploymentProviders() {
    const response = await this.api.get('/deployment/providers');
    return response.data;
  }

  async deployWorkspace(workspaceId: string, provider: string, config: any = {}) {
    const response = await this.api.post(
      `/deployment/deploy?workspace_id=${workspaceId}&provider=${provider}`,
      config
    );
    return response.data;
  }

  // Repo import + read
  async importStatus(workspaceId: string) {
    const response = await this.api.get(`/import/status?workspace_id=${workspaceId}`);
    return response.data;
  }

  async importGit(workspaceId: string, gitUrl: string, branch: string = 'main', token?: string) {
    const params = new URLSearchParams({
      workspace_id: workspaceId,
      git_url: gitUrl,
      branch,
    });
    if (token && token.trim()) {
      params.set('token', token.trim());
    }
    const response = await this.api.post(`/import/git?${params.toString()}`, {});
    return response.data;
  }

  async rehydrateImport(workspaceId: string, token?: string) {
    const params = new URLSearchParams({ workspace_id: workspaceId });
    if (token && token.trim()) {
      params.set('token', token.trim());
    }
    const response = await this.api.post(`/import/rehydrate?${params.toString()}`, {});
    return response.data;
  }

  async importTree(workspaceId: string) {
    const response = await this.api.get(`/import/tree?workspace_id=${workspaceId}`);
    return response.data;  }

  // A4 retrieval (RAG)
  async retrievalStatus() {
    const response = await this.api.get('/retrieval/status');
    return response.data;
  }

  async workspaceIndexStatus(workspaceId: string) {
    const response = await this.api.get(`/retrieval/workspace/${workspaceId}`);
    return response.data;
  }

  async workspaceIndexEnsure(workspaceId: string) {
    const response = await this.api.post(
      `/retrieval/workspace/${workspaceId}/ensure`,
      {}
    );
    return response.data;
  }

  async workspaceReindex(workspaceId: string, force: boolean = false) {
    const response = await this.api.post(
      `/retrieval/workspace/${workspaceId}/reindex?force=${force}`,
      {}
    );
    return response.data;
  }

  // ─── Deploy / Export ──────────────────────────────────────────────────
  async getDeployPlan(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/deploy/plan`);
    return response.data;
  }

  async cancelBuild(workspaceId: string) {
    const response = await this.api.delete(`/workspaces/${workspaceId}/build`);
    return response.data;
  }

  async runtimeStatus(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/runtime/status`);
    return response.data;
  }

  async runtimeStart(workspaceId: string) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/runtime/start`, {}
    );
    return response.data;
  }

  async runtimeStop(workspaceId: string) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/runtime/stop`, {}
    );
    return response.data;
  }

  /**
   * Sprint P0 Lanzamiento · Wake Preview.
   * Restarts an expired workspace runtime WITHOUT rebuilding and
   * WITHOUT consuming any credits. Idempotent — returns quickly
   * if the runtime is already running.
   */
  async runtimeWake(workspaceId: string) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/runtime/wake`, {}
    );
    return response.data;
  }

  /**
   * Sprint P0 Preview Roto · Wake ALL user's previews at once.
   * Server-side sweep that reactivates every workspace runtime for
   * the current user in one shot. No credits, no rebuild. Perfect
   * for calling on Home page mount so all recent projects are ready
   * before the user opens them.
   */
  async runtimeWakeAll() {
    const response = await this.api.post('/workspaces/runtime/wake-all', {});
    return response.data;
  }

  async pushWorkspaceToGithub(
    workspaceId: string,
    payload: {
      repo: string;
      branch?: string;
      commit_message?: string;
      create_repo?: boolean;
      private?: boolean;
    }
  ) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/export/github`,
      payload
    );
    return response.data;
  }

  /** Alias used by the chat SummaryCard Deploy button (semantic clarity). */
  async deployToGithub(
    workspaceId: string,
    payload: {
      repo: string;
      branch?: string;
      commit_message?: string;
      create_repo?: boolean;
      private?: boolean;
    }
  ) {
    return this.pushWorkspaceToGithub(workspaceId, payload);
  }

  async prepareRailway(workspaceId: string) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/railway/prepare`,
      {}
    );
    return response.data;
  }

  async readFile(workspaceId: string, path: string) {
    const response = await this.api.get(
      `/import/file?workspace_id=${workspaceId}&path=${encodeURIComponent(path)}`
    );
    return response.data;
  }

  // Memories
  async createMemory(data: any) {
    const response = await this.api.post('/memories', data);
    return response.data;
  }

  async listMemories(workspaceId: string, type?: string, tags?: string) {
    const params: any = { workspace_id: workspaceId };
    if (type) params.memory_type = type;
    if (tags) params.tags = tags;
    const response = await this.api.get('/memories', { params });
    return response.data;
  }

  async getMemory(id: string) {
    const response = await this.api.get(`/memories/${id}`);
    return response.data;
  }

  async deleteMemory(id: string) {
    const response = await this.api.delete(`/memories/${id}`);
    return response.data;
  }

  // Files
  async uploadFile(file: File, workspaceId?: string, missionId?: string, purpose: string = 'reference') {
    const formData = new FormData();
    formData.append('file', file);
    if (workspaceId) formData.append('workspace_id', workspaceId);
    if (missionId) formData.append('mission_id', missionId);
    formData.append('purpose', purpose);

    const response = await this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  getFileUrl(fileId: string): string {
    const token = localStorage.getItem('nexus_token');
    return `${API_BASE}/files/${fileId}/download?auth=${token}`;
  }

  async deleteFile(id: string) {
    const response = await this.api.delete(`/files/${id}`);
    return response.data;
  }

  // Health
  async healthCheck() {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Observability — every read goes through the same backend collection,
  // so a polling UI and a future SSE consumer would see identical data.
  async observabilitySummary(window: string = '24h', workspaceId?: string) {
    const params = new URLSearchParams({ window });
    if (workspaceId) params.set('workspace_id', workspaceId);
    const response = await this.api.get(`/observability/summary?${params.toString()}`);
    return response.data;
  }

  async observabilityEvents(opts: {
    limit?: number;
    cursor?: string;
    category?: string;
    workspaceId?: string;
    missionId?: string;
    status?: string;
    module?: string;
    search?: string;
    since?: string;
    until?: string;
  } = {}) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.category) params.set('category', opts.category);
    if (opts.workspaceId) params.set('workspace_id', opts.workspaceId);
    if (opts.missionId) params.set('mission_id', opts.missionId);
    if (opts.status) params.set('status', opts.status);
    if (opts.module) params.set('module', opts.module);
    if (opts.search) params.set('search', opts.search);
    if (opts.since) params.set('since', opts.since);
    if (opts.until) params.set('until', opts.until);
    const response = await this.api.get(`/observability/events?${params.toString()}`);
    return response.data;
  }

  async observabilityEvent(id: string) {
    const response = await this.api.get(`/observability/event/${id}`);
    return response.data;
  }

  async observabilityAlerts(window: string = '1h', workspaceId?: string) {
    const params = new URLSearchParams({ window });
    if (workspaceId) params.set('workspace_id', workspaceId);
    const response = await this.api.get(`/observability/alerts?${params.toString()}`);
    return response.data;
  }

  // Integrations — GitHub PAT persisted per user
  async getGithubIntegration() {
    const response = await this.api.get('/integrations/github');
    return response.data;
  }

  async saveGithubToken(token: string) {
    const response = await this.api.put('/integrations/github', { token });
    return response.data;
  }

  async deleteGithubToken() {
    const response = await this.api.delete('/integrations/github');
    return response.data;
  }

  // ─── Workspace Chat (conversational engine) ─────────────────────────
  async postConversationalTurn(
    message: string,
    opts: { history?: Array<{ role: string; content: string }>; has_project?: boolean } = {},
  ) {
    const response = await this.api.post(
      `/chat/conversational`,
      {
        message,
        history: opts.history || [],
        has_project: !!opts.has_project,
      },
    );
    return response.data as {
      mode: 'converse' | 'build';
      reply: string | null;
      confidence: number;
      reason: string;
      research_expected: boolean;
      answer_style: 'short' | 'detailed';
      credits_charged: number | null;
      session_id: string;
    };
  }

  async getWorkspaceConversation(workspaceId: string, since?: string) {
    const qs = since ? `?since=${encodeURIComponent(since)}` : '';
    const response = await this.api.get(`/workspaces/${workspaceId}/conversation${qs}`);
    return response.data;
  }

  async postWorkspaceMessage(
    workspaceId: string,
    text: string,
    attachments: any[] = [],
    wait: boolean = false,
  ) {
    const qs = wait ? '?wait=true' : '';
    const response = await this.api.post(
      `/workspaces/${workspaceId}/messages${qs}`,
      { text, attachments },
    );
    return response.data;
  }

  async resumeWorkspace(workspaceId: string, text: string, attachments: any[] = []) {
    const response = await this.api.post(
      `/workspaces/${workspaceId}/resume`,
      { text, attachments },
    );
    return response.data;
  }

  async pauseWorkspace(workspaceId: string) {
    const response = await this.api.post(`/workspaces/${workspaceId}/pause`);
    return response.data;
  }

  async clearWorkspaceConversation(workspaceId: string) {
    const response = await this.api.post(`/workspaces/${workspaceId}/clear`);
    return response.data;
  }

  async cancelWorkspace(workspaceId: string) {
    const response = await this.api.post(`/workspaces/${workspaceId}/cancel`);
    return response.data;
  }

  async uploadWorkspaceAttachment(workspaceId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const response = await this.api.post(
      `/workspaces/${workspaceId}/attachments`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async getWorkspaceFile(workspaceId: string, path: string) {
    const response = await this.api.get(
      `/workspaces/${workspaceId}/file`,
      { params: { path } },
    );
    return response.data;
  }

  async exportWorkspaceConversation(workspaceId: string) {
    const response = await this.api.get(`/workspaces/${workspaceId}/export`);
    return response.data;
  }

  // ─────────────── Nexus Self-Development (admin-only) ───────────────
  async selfBootstrap() {
    const response = await this.api.post('/self/bootstrap');
    return response.data;
  }

  async selfStatus(workspaceId: string) {
    const response = await this.api.get(`/self/status/${workspaceId}`);
    return response.data;
  }

  async selfPublish(
    workspaceId: string,
    commitMessage: string,
    branch: string = 'main',
  ) {
    const response = await this.api.post(`/self/publish/${workspaceId}`, {
      commit_message: commitMessage,
      branch,
      confirmed: true,
    });
    return response.data;
  }

  // ── Sprint v27 · Billing (Fase 2) ──────────────────────────────────────

  async getBillingBalance() {
    const r = await this.api.get('/billing/balance');
    return r.data as {
      credits_balance: number;
      equivalent_usd: number;
      status: 'free' | 'paid';
      welcome_offer_available: boolean;
    };
  }

  async getBillingInfo() {
    // Aggregates balance + packages for the /creditos page.
    const [balance, packages] = await Promise.all([
      this.getBillingBalance().catch(() => null),
      this.api.get('/billing/packages').then(r => r.data).catch(() => ({ packages: [] })),
    ]);
    return {
      balance: balance?.credits_balance ?? 0,
      packages: (packages?.packages || packages || []).map((p: any) => ({
        id: p.id,
        credits: p.credits,
        price_usd: Number(p.price_usd || p.amount_usd || 0),
        is_welcome: !!p.is_welcome,
      })),
    };
  }

  async getBillingLedger(limit = 50, beforeId?: string) {
    const params: Record<string, string | number> = { limit };
    if (beforeId) params.before_id = beforeId;
    const r = await this.api.get('/billing/ledger', { params });
    return r.data as Array<{
      id: string;
      delta: number;
      balance_after: number;
      reason: string;
      reference_id?: string;
      metadata: Record<string, unknown>;
      created_at: string;
    }>;
  }

  async getBillingPackages() {
    const r = await this.api.get('/billing/packages');
    return r.data as {
      credit_value_usd: number;
      welcome_offer_used?: boolean;
      packages: Array<{
        id: string;
        credits: number;
        price_usd: number;
        amount_cents: number;
        label: string;
        description: string;
        welcome_only: boolean;
        featured?: boolean;
      }>;
    };
  }

  async getWelcomeOffer() {
    const r = await this.api.get('/billing/welcome-offer');
    return r.data as {
      available: boolean;
      should_show: boolean;
      used: boolean;
      package: {
        id: string;
        credits: number;
        price_usd: number;
        amount_cents: number;
        label: string;
      };
    };
  }

  async getBillingConfig() {
    const r = await this.api.get('/billing/config');
    return r.data as {
      publishable_key: string | null;
      live_mode: boolean;
      configured: boolean;
    };
  }

  // ── Admin v30 endpoints ────────────────────────────────────────────
  async getAdminFinancial(days: number = 30) {
    const r = await this.api.get(`/admin/financial/dashboard?days=${days}`);
    return r.data as any;
  }

  async getAdminUsers(search: string = '', limit: number = 100) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (search) qs.set('search', search);
    const r = await this.api.get(`/admin/users?${qs.toString()}`);
    return r.data as { users: any[]; count: number };
  }

  async getAdminUserUsage(userId: string, limit: number = 100) {
    const r = await this.api.get(`/admin/users/${userId}/usage?limit=${limit}`);
    return r.data as { events: any[]; count: number };
  }

  async getAdminPricing() {
    const r = await this.api.get('/admin/pricing');
    return r.data as { config: any; models: any[] };
  }

  async updateAdminPricingConfig(body: any) {
    const r = await this.api.put('/admin/pricing/config', body);
    return r.data;
  }

  async upsertAdminModelPrice(model: any) {
    const r = await this.api.put(`/admin/pricing/models/${encodeURIComponent(model.model)}`, model);
    return r.data;
  }

  async listAdminTemplates() {
    try {
      const r = await this.api.get('/admin/templates');
      return r.data as { templates: any[] };
    } catch {
      return { templates: [] };
    }
  }

  async createAdminTemplate(body: any) {
    const r = await this.api.post('/admin/templates', body);
    return r.data;
  }

  async updateAdminTemplate(id: string, body: any) {
    const r = await this.api.put(`/admin/templates/${id}`, body);
    return r.data;
  }

  async deleteAdminTemplate(id: string) {
    const r = await this.api.delete(`/admin/templates/${id}`);
    return r.data;
  }

  async duplicateAdminTemplate(id: string) {
    const r = await this.api.post(`/admin/templates/${id}/duplicate`);
    return r.data;
  }

  async setAdminTemplateActive(id: string, isActive: boolean) {
    const r = await this.api.patch(`/admin/templates/${id}/active`, { is_active: isActive });
    return r.data;
  }

  // ── v31 · Admin self-recharge ─────────────────────────────────────
  async adminSelfRecharge(packageId: string) {
    const r = await this.api.post('/admin/self/recharge', { package_id: packageId });
    return r.data as { credits_added: number; simulated_price_usd: number; balance_after: number; remaining_recharges_today: number };
  }

  async adminSelfRecharges() {
    const r = await this.api.get('/admin/self/recharges');
    return r.data as { recharges: any[]; count: number };
  }

  // ── v31 · TTS ──────────────────────────────────────────────────────
  async getTtsVoices() {
    const r = await this.api.get('/tts/voices');
    return r.data as { voices: any[]; default_voice_id: string };
  }

  async setDefaultVoice(voice_id: string) {
    const r = await this.api.put('/tts/voices/default', { voice_id });
    return r.data;
  }

  async synthesizeTts(text: string, voice_id?: string, speed?: number) {
    const r = await this.api.post('/tts/synthesize', { text, voice_id, speed });
    return r.data as { audio_base64: string; voice_id: string; voice_name: string; content_type: string; chars: number };
  }

  /**
   * Streaming TTS — returns a Blob (audio/mpeg). Feed it to
   * `new Audio(URL.createObjectURL(blob))` for instant Play/Pause/Stop.
   */
  async streamTts(text: string, voice_id?: string, speed?: number): Promise<Blob> {
    const r = await this.api.post('/tts/stream', { text, voice_id, speed }, {
      responseType: 'blob',
    });
    return r.data as Blob;
  }

  /**
   * Sprint UX Workspace v1.34 · lista curada de voces + voz predeterminada.
   */
  async getTTSVoices(): Promise<{ voices: Array<{ id: string; name: string; description?: string; personality?: string; provider?: string }>; default_voice_id: string }> {
    const r = await this.api.get('/tts/voices');
    return r.data;
  }

  /**
   * Sprint Cierre v1.28 · #12 — TTS in-memory cache.
   * La misma sesión no re-sintetiza dos veces el mismo texto.
   * Devuelve un object URL reutilizable.
   */
  private _ttsCache: Map<string, string> = new Map();

  async getTTSUrl(text: string): Promise<string | null> {
    if (!text || !text.trim()) return null;
    const key = text.slice(0, 2400);
    const cached = this._ttsCache.get(key);
    if (cached) return cached;
    try {
      const blob = await this.streamTts(key);
      const url = URL.createObjectURL(blob);
      this._ttsCache.set(key, url);
      // Cap the cache to 40 entries to avoid unbounded memory growth.
      if (this._ttsCache.size > 40) {
        const first = this._ttsCache.keys().next().value;
        if (first) {
          const oldUrl = this._ttsCache.get(first);
          if (oldUrl) URL.revokeObjectURL(oldUrl);
          this._ttsCache.delete(first);
        }
      }
      return url;
    } catch {
      return null;
    }
  }

  // ── v1.0 · Modo Taller ─────────────────────────────────────────────
  async createWorkshopSession(repo_url: string) {
    const r = await this.api.post('/workshop/sessions', { repo_url });
    return r.data;
  }
  async listWorkshopSessions() {
    const r = await this.api.get('/workshop/sessions');
    return r.data as { sessions: any[]; count: number };
  }
  async getWorkshopSession(sid: string) {
    const r = await this.api.get(`/workshop/sessions/${sid}`);
    return r.data;
  }
  async deleteWorkshopSession(sid: string) {
    const r = await this.api.delete(`/workshop/sessions/${sid}`);
    return r.data;
  }
  async provideWorkshopEnv(sid: string, values: Record<string, string>) {
    const r = await this.api.post(`/workshop/sessions/${sid}/env`, { values });
    return r.data;
  }
  async startWorkshopRepair(sid: string) {
    const r = await this.api.post(`/workshop/sessions/${sid}/repair`);
    return r.data;
  }

  // ── v31 · Support Center ───────────────────────────────────────────
  async searchSupportUsers(q: string) {
    const r = await this.api.get(`/admin/support/users/search?q=${encodeURIComponent(q)}`);
    return r.data as { users: any[] };
  }

  async getSupportUserWorkspaces(userId: string) {
    const r = await this.api.get(`/admin/support/users/${userId}/workspaces`);
    return r.data as { workspaces: any[] };
  }

  async analyzeSupportTicket(body: { user_id: string; workspace_id?: string | null; ticket_message: string }) {
    const r = await this.api.post('/admin/support/analyze', body);
    return r.data;
  }

  // ── Sprint P0 v1.43 · Chat de soporte del usuario ────────────────
  async getSupportChatThread() {
    const r = await this.api.get('/support/chat/thread');
    return r.data as {
      thread_id: string;
      status: string;
      messages: Array<{ id: string; role: 'user' | 'assistant' | 'agent'; content: string; created_at: string }>;
    };
  }

  async sendSupportChatMessage(content: string) {
    const r = await this.api.post('/support/chat/messages', { content });
    return r.data as {
      ok: boolean;
      thread_id: string;
      messages: Array<{ id: string; role: 'user' | 'assistant' | 'agent'; content: string; created_at: string }>;
    };
  }

  async closeSupportChatThread() {
    const r = await this.api.post('/support/chat/close');
    return r.data;
  }

  // ── Sprint Arquitecto IA (Feb 2026) · Nexus Architect ────────────
  async architectChat(body: {
    message: string;
    workspace_id?: string | null;
    attachments?: Array<{ name?: string; data_url?: string; url?: string; kind?: string }>;
  }) {
    const r = await this.api.post('/architect/chat', {
      message: body.message,
      workspace_id: body.workspace_id || undefined,
      attachments: body.attachments || [],
    });
    return r.data as {
      workspace_id: string;
      assistant_message: string;
      intent: 'chat' | 'question' | 'build' | 'modify';
      trigger_builder: boolean;
      builder_spec: any | null;
      follow_up_questions: string[];
      delivery_card: any | null;
    };
  }

  async getArchitectSession(workspaceId: string) {
    const r = await this.api.get(`/architect/session/${workspaceId}`);
    return r.data as {
      workspace_id: string;
      workspace_name: string;
      turns: Array<{ role: 'user' | 'assistant'; content: string; at: string; intent?: string }>;
      project_memory: Record<string, any>;
      last_spec: any | null;
      delivery_cards: any[];
    };
  }

  async architectDeliveryCard(workspaceId: string, buildSummary?: string) {
    const r = await this.api.post('/architect/delivery-card', {
      workspace_id: workspaceId,
      build_summary: buildSummary,
    });
    return r.data as { delivery_card: any };
  }

  async architectTriggerBuild(workspaceId: string) {
    const r = await this.api.post('/architect/trigger-build', {
      workspace_id: workspaceId,
    });
    return r.data as { status: string; workspace_id: string };
  }

  /**
   * Sprint Final P0 (Feb 2026) — Resume a build that paused because
   * the user ran out of credits. Backend validates the balance BEFORE
   * dispatching the Builder: 402 when still zero, 200 otherwise.
   */
  async resumeArchitect(workspaceId: string) {
    const r = await this.api.post(`/architect/resume/${workspaceId}`);
    return r.data as { status: string; workspace_id: string; balance: number };
  }

  async createCheckoutSession(
    packageId: string,
    opts?: { returnTo?: string },
  ) {
    const r = await this.api.post('/billing/checkout/create', {
      package_id: packageId,
      origin_url: typeof window !== 'undefined' ? window.location.origin : undefined,
      return_to: opts?.returnTo,
    });
    return r.data as {
      session_id: string;
      checkout_url: string;
      amount_cents: number;
      credits: number;
      welcome_only: boolean;
      mock: boolean;
      live_mode: boolean;
    };
  }

  async getCheckoutStatus(sessionId: string) {
    const r = await this.api.get(`/billing/checkout/status/${sessionId}`);
    return r.data as {
      session_id: string;
      state: 'pending' | 'completed' | 'failed';
      package_id: string;
      credits: number;
      amount_cents: number;
      welcome_only: boolean;
      created_at: string;
      completed_at?: string;
    };
  }

  async mockCompleteCheckout(sessionId: string) {
    const r = await this.api.post(
      `/billing/mock/complete-checkout/${sessionId}`,
    );
    return r.data;
  }

  // Sprint Arquitectura Definitiva #14 · Sync tras volver de Stripe.
  // Fuerza la acreditación inmediata sin depender del webhook.
  async syncCheckout(sessionId: string) {
    const r = await this.api.post(`/billing/checkout/sync/${sessionId}`);
    return r.data as {
      status: 'processed' | 'already_completed' | 'already_processed' | 'pending';
      session_id: string;
      credits_added: number;
      balance?: number;
    };
  }

  // Sprint Corrección Final — admin path para acreditar créditos sin Stripe.
  async grantCreditsToUser(userId: string, amount: number, reason?: string) {
    const r = await this.api.post(`/admin/users/${userId}/grant`, {
      amount, reason: reason || 'admin grant',
    });
    return r.data as { status: string; ledger_entry_id?: string; amount: number };
  }

  // Sprint Corrección Final — checkout con monto personalizado (USD).
  async createCustomCheckout(amountUsd: number, opts?: { returnTo?: string }) {
    const r = await this.api.post('/billing/checkout/create', {
      custom_amount_usd: amountUsd,
      origin_url: typeof window !== 'undefined' ? window.location.origin : undefined,
      return_to: opts?.returnTo,
    });
    return r.data as {
      session_id: string;
      checkout_url?: string;
      credits: number;
      amount_cents: number;
      mock?: boolean;
      live_mode?: boolean;
    };
  }

  async listMyApps() {
    const r = await this.api.get('/apps');
    return r.data as Array<{
      id: string;
      workspace_id: string;
      tier: string;
      tier_credits_monthly: number;
      tier_label: string;
      state: string;
      daily_charge_credits: number;
      public_url?: string;
      grace_period_days_remaining?: number;
      created_at: string;
    }>;
  }

  async pauseMyApp(appId: string) {
    const r = await this.api.post(`/apps/${appId}/pause`);
    return r.data;
  }

  async resumeMyApp(appId: string) {
    const r = await this.api.post(`/apps/${appId}/resume`);
    return r.data;
  }

  async changeAppTier(appId: string, tier: string) {
    const r = await this.api.post(`/apps/${appId}/change-tier`, { tier });
    return r.data;
  }
}


export const apiService = new ApiService();
export default apiService;
